/* ============================================================
   search.js — suggesties tijdens het typen.

   Iconic draait Klevu. Wij gebruiken Shopify's eigen predictive search: dezelfde
   plek in de header, dezelfde markup, geen app. Het formulier werkt zonder
   JavaScript (het post naar /search); dit script vult alleen het resultatenblok.

   Er wordt pas gezocht vanaf twee tekens en met 250 ms wachttijd, zodat je niet
   bij elke toetsaanslag een verzoek stuurt.

   Toegankelijkheid (04-09-2026). Het invoerveld in `sections/header.liquid`
   draagt `role="combobox"` met `aria-haspopup="listbox"` en
   `aria-controls="predictive-search-results"`; dat blok draagt `role="listbox"`.
   Die combinatie belooft het toetsenbordgedrag van een combobox, en dat stond
   er niet — pijltjes deden niets en er was geen `aria-activedescendant`. Een
   rol zonder dat gedrag is voor een schermlezergebruiker slechter dan geen rol,
   dus staat het hier nu wél:

     ↓ / ↑        volgende / vorige optie, met omslag aan de uiteinden
     Enter        volgt de gemarkeerde optie; zonder markering post het
                  formulier gewoon naar /search (het normale zoekgedrag)
     Escape       sluit de lijst, wist de markering, laat de tekst staan
     Home / End   eerste / laatste optie

   De focus blijft daarbij in het invoerveld staan — dat is het punt van
   `aria-activedescendant`: de gebruiker houdt zijn cursor en kan blijven typen.
   De opties zijn `<li role="option">` met een `<a tabindex="-1">` erin, zodat
   de muis en middelklik werken zonder dat de links een tweede tabvolgorde
   openen naast de lijst.
   ============================================================ */
(() => {
  const form = document.querySelector('.js-search-form');
  const invoer = form?.querySelector('.js-search-input');
  const resultaten = document.querySelector('#predictive-search-results');
  if (!form || !invoer || !resultaten) return;

  let timer = null;
  let lopend = null;

  /* Eén beleefde live-regio voor de status. Hij hangt náást het resultatenblok
     en niet erin: alles binnen `role="listbox"` dat geen `option` is, valt bij
     een schermlezer buiten de lijst. */
  const status = document.createElement('span');
  status.className = 'sr-only';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  resultaten.insertAdjacentElement('afterend', status);

  const opties = () => [...resultaten.querySelectorAll('[role="option"]:not([aria-disabled="true"])')];
  const gemarkeerd = () => resultaten.querySelector('[role="option"][aria-selected="true"]');

  const wisMarkering = () => {
    resultaten.querySelectorAll('[role="option"][aria-selected="true"]')
      .forEach((o) => o.setAttribute('aria-selected', 'false'));
    invoer.removeAttribute('aria-activedescendant');
  };

  const markeer = (optie) => {
    if (!optie) return;
    wisMarkering();
    optie.setAttribute('aria-selected', 'true');
    if (!optie.id) optie.id = `zoek-optie-${Math.round(performance.now() * 1000)}`;
    invoer.setAttribute('aria-activedescendant', optie.id);
    optie.scrollIntoView({ block: 'nearest' });
  };

  const verplaats = (richting) => {
    const lijst = opties();
    if (!lijst.length) return;
    const nu = lijst.indexOf(gemarkeerd());
    const volgende = nu === -1
      ? (richting === 1 ? 0 : lijst.length - 1)
      : (nu + richting + lijst.length) % lijst.length;
    markeer(lijst[volgende]);
  };

  const toon = (aan) => {
    resultaten.classList.toggle('hidden', !aan);
    invoer.setAttribute('aria-expanded', String(aan));
    if (!aan) {
      wisMarkering();
      status.textContent = '';
    }
  };

  async function zoek(term) {
    lopend?.abort();
    lopend = new AbortController();
    try {
      const url = `${window.Shopify?.routes?.root || '/'}search/suggest?q=${encodeURIComponent(term)}`
        + `&resources[type]=product,page&resources[limit]=6&section_id=predictive-search`;
      const antwoord = await fetch(url, { signal: lopend.signal });
      const tekst = await antwoord.text();
      const doc = new DOMParser().parseFromString(tekst, 'text/html');
      const inhoud = doc.querySelector('#predictive-search-results') || doc.body;
      resultaten.innerHTML = inhoud.innerHTML;
      wisMarkering();
      toon(true);
      /* Het aantal komt uit de sectie mee als verborgen tekst; is het er niet
         (oude cache, andere sjabloon), dan tellen we de opties zelf. */
      const aantal = resultaten.querySelector('[data-zoek-aantal]');
      status.textContent = aantal ? aantal.textContent.trim() : String(opties().length);
    } catch (e) {
      if (e.name !== 'AbortError') toon(false);
    }
  }

  invoer.addEventListener('input', () => {
    const term = invoer.value.trim();
    clearTimeout(timer);
    if (term.length < 2) { toon(false); return; }
    timer = setTimeout(() => zoek(term), 250);
  });

  document.addEventListener('click', (e) => {
    if (!form.contains(e.target)) toon(false);
  });

  invoer.addEventListener('keydown', (e) => {
    const uitgeklapt = invoer.getAttribute('aria-expanded') === 'true';
    switch (e.key) {
      case 'Escape':
        toon(false);
        break;
      case 'ArrowDown':
        if (!uitgeklapt) return;
        e.preventDefault();
        verplaats(1);
        break;
      case 'ArrowUp':
        if (!uitgeklapt) return;
        e.preventDefault();
        verplaats(-1);
        break;
      case 'Home':
        if (!uitgeklapt || !opties().length) return;
        e.preventDefault();
        markeer(opties()[0]);
        break;
      case 'End':
        if (!uitgeklapt || !opties().length) return;
        e.preventDefault();
        markeer(opties()[opties().length - 1]);
        break;
      case 'Enter': {
        /* Zonder markering laten we het formulier zijn werk doen: dan is Enter
           gewoon "zoek hierop" en beland je op /search. */
        const link = gemarkeerd()?.querySelector('a[href]');
        if (!link) return;
        e.preventDefault();
        link.click();
        break;
      }
      default:
        break;
    }
  });

  /* De muis markeert mee, zodat wat je aanwijst en wat de combobox "actief"
     noemt niet uit elkaar lopen. */
  resultaten.addEventListener('pointermove', (e) => {
    const optie = e.target.closest?.('[role="option"]:not([aria-disabled="true"])');
    if (optie && optie !== gemarkeerd()) markeer(optie);
  });
})();
